import React, { useState, useEffect } from "react";
import "./DynamicPersonsToVisitTable.css";

interface PersonToVisitEntry {
  name: string;
  relationship: string;
  address: string;
}

interface DynamicPersonsToVisitTableProps {
  value: { [key: string]: PersonToVisitEntry };
  onChange: (entries: { [key: string]: PersonToVisitEntry }) => void;
  required?: boolean;
}

const DynamicPersonsToVisitTable: React.FC<DynamicPersonsToVisitTableProps> = ({
  value,
  onChange,
  required = false,
}) => {
  const [entries, setEntries] = useState<{ [key: string]: PersonToVisitEntry }>(
    value || {}
  );

  useEffect(() => {
    setEntries(value || {});
  }, [value]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === "Enter") {
        event.preventDefault();
        addEntry();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const addEntry = () => {
    const entryCount = Object.keys(entries).length;
    const newKey = `personToVisit${entryCount + 1}`;

    const newEntry: PersonToVisitEntry = {
      name: "",
      relationship: "",
      address: "",
    };

    const updatedEntries = { ...entries, [newKey]: newEntry };
    setEntries(updatedEntries);
    onChange(updatedEntries);
  };

  const removeEntry = (key: string) => {
    const { [key]: removed, ...remainingEntries } = entries;

    // Renumber the remaining entries
    const renumberedEntries: { [key: string]: PersonToVisitEntry } = {};
    const remainingKeys = Object.keys(remainingEntries).sort();

    remainingKeys.forEach((oldKey, index) => {
      const newKey = `personToVisit${index + 1}`;
      renumberedEntries[newKey] = remainingEntries[oldKey];
    });

    setEntries(renumberedEntries);
    onChange(renumberedEntries);
  };

  const updateEntry = (
    key: string,
    field: keyof PersonToVisitEntry,
    value: string
  ) => {
    const updatedEntries = {
      ...entries,
      [key]: { ...entries[key], [field]: value },
    };
    setEntries(updatedEntries);
    onChange(updatedEntries);
  };

  // Initialize with at least one entry if empty
  useEffect(() => {
    if (Object.keys(entries).length === 0) {
      addEntry();
    }
  }, []);

  const entryKeys = Object.keys(entries).sort();

  return (
    <div className="dynamic-persons-to-visit-table">
      <div className="table-header">
        <h4>Persons or Institutions to Visit</h4>
        <button
          type="button"
          className="add-btn"
          onClick={addEntry}
          title="Add person/institution entry (Ctrl+Enter)"
        >
          + Add Person/Institution Entry
        </button>
      </div>

      {entryKeys.length === 0 ? (
        <div className="empty-state">
          <p>No persons or institutions entered yet.</p>
          <button type="button" className="add-btn" onClick={addEntry}>
            + Add Person/Institution Entry
          </button>
        </div>
      ) : (
        <div className="entries-container">
          {entryKeys.map((key, index) => (
            <div key={key} className="entry-card">
              <div className="entry-header">
                <div className="entry-number">
                  <span className="entry-badge">{index + 1}</span>
                  <h5>Entry {index + 1}</h5>
                </div>
                <button
                  type="button"
                  className="remove-btn"
                  onClick={() => removeEntry(key)}
                  title="Remove this person/institution entry"
                >
                  ×
                </button>
              </div>

              <div className="entry-form">
                <div className="form-row form-row-1">
                  <div className="field-group">
                    <label>Name</label>
                    <input
                      type="text"
                      value={entries[key].name}
                      onChange={(e) => updateEntry(key, "name", e.target.value)}
                      placeholder="e.g., John Doe"
                    />
                  </div>

                  <div className="field-group">
                    <label>Relationship</label>
                    <input
                      type="text"
                      value={entries[key].relationship}
                      onChange={(e) =>
                        updateEntry(key, "relationship", e.target.value)
                      }
                      placeholder="e.g., Brother, Friend, University"
                    />
                  </div>
                </div>

                <div className="form-row form-row-2">
                  <div className="field-group field-group-wide">
                    <label>Address</label>
                    <input
                      type="text"
                      value={entries[key].address}
                      onChange={(e) =>
                        updateEntry(key, "address", e.target.value)
                      }
                      placeholder="e.g., 115 Sherbrooke Street West"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {entryKeys.length > 0 && (
        <div className="table-footer">
          <button
            type="button"
            className="add-btn"
            onClick={addEntry}
            title="Add another person/institution entry (Ctrl+Enter)"
          >
            + Add Another Person/Institution Entry
          </button>
        </div>
      )}

      {required && entryKeys.length === 0 && (
        <small className="error-text">
          At least one person or institution entry is required
        </small>
      )}
    </div>
  );
};

export default DynamicPersonsToVisitTable;
